import * as Sentry from "@sentry/react";
import { Centrifuge } from "centrifuge";
import { useEffect, useState, useRef } from "react";
import moment from "moment";

import { useWSToken } from "../Messaging/WSTokenProvider";

import { API_URL, ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { LOCAL_STORAGE } from "@/constants";
import { WSTokenResponse } from "@/queries/websocket/useWSTokenRequest";

const useCentrifugeClient = () => {
  const [client, setClient] = useState<Centrifuge | null>(null);
  const { data, setTokenDetails } = useWSToken();
  const token = data?.token_details?.token;
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<Centrifuge | null>(null);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    // Wait until we have a valid WS token before connecting.
    // After sign-in, the token arrives asynchronously; without this guard
    // the client would connect with an undefined token and silently fail.
    if (clientRef.current || !token) {
      return;
    }

    const url = new URL(API_URL);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/connection/websocket";

    const EXPIRY_BUFFER_SECONDS = 30;

    const centrifugeClient = new Centrifuge(url.toString(), {
      token,
      getToken: async () => {
        const currentData = dataRef.current;

        if (
          currentData?.token_details?.expires &&
          currentData.token_details.expires >= moment().unix() + EXPIRY_BUFFER_SECONDS
        ) {
          return currentData.token_details.token;
        }

        try {
          const tokenData = (await fetch(ENDPOINTS.ws_token_request, {
            method: "POST",
            headers: jsonAuthHeaders(localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN) ?? ""),
          }).then(handleResponse)) as WSTokenResponse;

          setTokenDetails(tokenData);

          return tokenData?.token_details?.token ?? "";
        } catch (error) {
          Sentry.captureException(error, {
            tags: { event_type: "ws_token_refresh_failure" },
          });
          // Return the stale token instead of "". An empty string causes a
          // fast reconnect loop that breaks delta encoding and kills the
          // subscription. The stale token lets Centrifuge back off naturally.
          return currentData?.token_details?.token ?? "";
        }
      },
      minReconnectDelay: 3000,
      maxReconnectDelay: 60000,
      maxServerPingDelay: 10000,
    });

    centrifugeClient.on("connected", () => {
      setIsConnected(true);
    });

    centrifugeClient.on("disconnected", () => {
      setIsConnected(false);
    });

    clientRef.current = centrifugeClient;
    setClient(centrifugeClient);
    centrifugeClient.connect();

    return () => {
      centrifugeClient.disconnect();
      setClient(null);
      clientRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { client, isConnected };
};

export { useCentrifugeClient };
